import { BarberId, PriceId } from './business'

export type Language = 'es' | 'en'

export type SiteContent = {
  meta: { title: string; description: string }
  nav: {
    about: string
    services: string
    barbers: string
    gallery: string
    how: string
    book: string
    location: string
  }
  barbers: {
    eyebrow: string
    title: string
    intro: string
    roles: Record<BarberId, string>
    bookWith: string
    tapHint: string
    selected: string
    clear: string
  }
  header: { locationLabel: string; call: string; openMenu: string; closeMenu: string; langSwitch: string }
  hero: {
    eyebrow: string
    titleTop: string
    titleAccent: string
    subtitleLead: string
    subtitleStrong: string
    ctaBook: string
    ctaCall: string
    facts: string[]
    imageAlt: string
  }
  about: {
    eyebrow: string
    title: string
    p1: string
    p2: string
    highlight: string
    imageAlt: string
    values: { title: string; text: string }[]
  }
  services: {
    eyebrow: string
    title: string
    intro: string
    badge: string
    priceVaries: string
    imageAlt: string
    note: string
    items: Record<PriceId, { title: string; detail?: string }>
  }
  gallery: {
    eyebrow: string
    title: string
    intro: string
    regionLabel: string
    photos: { caption: string; alt: string }[]
  }
  steps: { eyebrow: string; title: string; stepLabel: string; items: { title: string; description: string }[] }
  booking: {
    eyebrow: string
    title: string
    intro: string
    callLabel: string
    callAction: string
    whatsappLabel: string
    whatsappAction: string
    walkIn: string
    labels: {
      name: string
      phone: string
      barber: string
      service: string
      date: string
      time: string
      notes: string
    }
    anyBarber: string
    placeholders: { name: string; phone: string; notes: string }
    serviceOptions: string[]
    submit: string
    sending: string
    disclaimer: string
    success: {
      title: string
      thanks: string
      neighbor: string
      confirmA: string
      confirmB: string
      chosenDay: string
      with: string
      again: string
    }
  }
  location: {
    eyebrow: string
    title: string
    mapTitle: string
    directions: string
    hoursTitle: string
    today: string
    days: string[]
    closed: string
    languageNote: string
  }
  testimonials: {
    eyebrow: string
    title: string
    ratingLabel: string
    items: { name: string; role: string; quote: string }[]
  }
  finalCta: { title: string; body: string; callPrefix: string; note: string }
  footer: {
    about: string
    contactTitle: string
    hoursShort: string
    sectionsTitle: string
    areaTitle: string
    areaText: string
    rights: string
    languages: string
  }
  mobileBar: { call: string; book: string }
}

export const content: Record<Language, SiteContent> = {
  es: {
    meta: {
      title: 'Haircuts Factory | Barbería Familiar en Dundee, FL',
      description:
        'Barbería familiar en Dundee, FL. Cortes para niños y adultos, fades, barba y diseños. Con cita o por orden de llegada. Hablamos español. Llama al +1 787-429-0831.',
    },
    nav: {
      about: 'Quiénes somos',
      services: 'Servicios',
      barbers: 'Barberos',
      gallery: 'Galería',
      how: 'Cómo funciona',
      book: 'Agendar',
      location: 'Ubicación',
    },
    barbers: {
      eyebrow: 'Nuestro equipo',
      title: 'Elige tu barbero',
      intro:
        'Toca la foto del barbero con quien te quieres cortar y te llevamos al formulario con su nombre ya seleccionado.',
      roles: {
        alex: 'Dueño · Cortes de niños y fades',
        gardy: 'Barbero · Fades y clásicos',
        michael: 'Barbero · Barba y diseños',
        cuba: 'Barbero · Degradados',
        yady: 'Barbero · Fades y perfilado',
        malvin: 'Barbero · Rizos y diseños',
      },
      bookWith: 'Agendar con',
      tapHint: 'Toca para agendar',
      selected: 'Seleccionado',
      clear: 'Quitar selección',
    },
    header: {
      locationLabel: 'Dundee, FL',
      call: 'Llámanos',
      openMenu: 'Abrir menú',
      closeMenu: 'Cerrar menú',
      langSwitch: 'Ver en inglés',
    },
    hero: {
      eyebrow: 'Barbería familiar · Dundee, FL',
      titleTop: 'Tu barbería de confianza en Dundee',
      titleAccent: 'para toda la familia',
      subtitleLead:
        'Cortes profesionales para niños y adultos: fades, tijera, barba y diseños. Con cita o sin cita.',
      subtitleStrong: '¡Hablamos español!',
      ctaBook: 'Reserva tu turno hoy',
      ctaCall: 'Llámanos ahora',
      facts: ['Español e inglés', 'Con cita o por orden de llegada', 'Lunes a sábado'],
      imageAlt: 'Interior de Haircuts Factory con sus sillas de barbero y luces LED',
    },
    about: {
      eyebrow: 'Quiénes somos',
      title: 'Somos una barbería familiar',
      p1: 'Haircuts Factory nació de algo sencillo: queríamos un lugar donde una familia completa pudiera sentarse cómoda, entenderse en su propio idioma y salir con un corte del que estuviera orgullosa.',
      p2: 'Aquí se atiende igual al papá que llega de prisa antes del trabajo, al nene del primer corte y al abuelo de siempre. Buen corte, conversación y un ambiente tranquilo para todos.',
      highlight:
        'Te atendemos en español o en inglés, como prefieras: puedes explicar exactamente cómo quieres tu corte, sin señas ni malentendidos.',
      imageAlt: 'Interior de Haircuts Factory con sus sillas de barbero e iluminación LED',
      values: [
        { title: 'Familia', text: 'Aquí entra el abuelo, el papá y el nene. Los tres salen contentos.' },
        {
          title: 'Comunidad',
          text: 'Somos vecinos de Main Street y conocemos a nuestros clientes por su nombre.',
        },
        {
          title: 'Confianza',
          text: 'Herramientas desinfectadas, trabajo prolijo y precios claros desde el principio.',
        },
      ],
    },
    services: {
      eyebrow: 'Servicios y precios',
      title: 'Servicios y precios',
      intro:
        'Precios claros desde el principio. Todos los servicios incluyen perfilado de contornos y un ambiente relajado, sin apuros.',
      badge: 'Premium',
      priceVaries: 'Precio varía',
      imageAlt: 'Lista de precios de Haircuts Factory',
      note: 'Los precios pueden variar según el largo y el tipo de cabello. Pregúntanos sin compromiso.',
      items: {
        haircut: { title: 'Corte de cabello' },
        haircutBeardTowel: { title: 'Corte + barba', detail: 'Con toalla caliente' },
        haircutBeardSteamer: { title: 'Corte + barba', detail: 'Toalla caliente y vaporizador' },
        kids: { title: 'Corte de niño' },
        beardEyebrows: { title: 'Barba + cejas' },
        eyebrows: { title: 'Cejas' },
        senior: { title: 'Especial 3ra edad', detail: 'Solo los miércoles' },
        shampoo: { title: 'Shampoo' },
        vip: {
          title: 'Servicio VIP',
          detail: 'Corte, afeitado con toalla caliente y vaporizador, masaje y mascarilla negra',
        },
        platinum: { title: 'Cabello platinado', detail: 'Incluye el corte' },
      },
    },
    gallery: {
      eyebrow: 'Nuestros trabajos',
      title: 'Cortes hechos en nuestra silla',
      intro: 'Fotos reales de nuestro trabajo. Trae la referencia que quieras y te la hacemos igual.',
      regionLabel: 'Carrusel de fotos de cortes realizados',
      photos: [
        { caption: 'El dueño en acción', alt: 'El propietario de Haircuts Factory atendiendo a un cliente' },
        { caption: 'Fade alto con barba perfilada', alt: 'Cliente adulto con fade alto y barba perfilada' },
        { caption: 'Trabajo de máquina y peine', alt: 'Barbero trabajando con la máquina en el corte de un cliente' },
        { caption: 'Degradado con rizos definidos', alt: 'Corte con degradado y rizos definidos en la parte superior' },
        { caption: 'Taper clásico y línea limpia', alt: 'Vista de espaldas de un corte clásico con taper limpio' },
      ],
    },
    steps: {
      eyebrow: 'Cómo funciona',
      title: 'Tres pasos y estás en la silla',
      stepLabel: 'Paso',
      items: [
        {
          title: 'Llámanos o escríbenos',
          description:
            'Marca el +1 787-429-0831 o mándanos un WhatsApp. También puedes agendar aquí mismo en el formulario.',
        },
        {
          title: 'Con cita o sin cita',
          description:
            'Si vienes con cita, te atendemos a tu hora. Si pasas de sorpresa, te recibimos por orden de llegada.',
        },
        {
          title: '¡Listo, sal con un gran corte!',
          description: 'Te sientas, conversamos y sales con el corte que querías. Así de simple.',
        },
      ],
    },
    booking: {
      eyebrow: 'Agenda tu cita',
      title: 'Reserva tu turno hoy',
      intro:
        'Déjanos tus datos y te confirmamos por llamada o WhatsApp el mismo día. Si prefieres hablar con nosotros directamente, marca y te atendemos.',
      callLabel: 'Llámanos',
      callAction: 'Llamar ahora',
      whatsappLabel: 'WhatsApp',
      whatsappAction: 'Escríbenos ahora',
      walkIn:
        'También atendemos por orden de llegada, de lunes a sábado. No necesitas cita para pasar.',
      labels: {
        name: 'Nombre completo',
        phone: 'Teléfono',
        barber: 'Barbero',
        service: 'Servicio',
        date: 'Fecha',
        time: 'Hora',
        notes: '¿Algo que debamos saber? (opcional)',
      },
      anyBarber: 'Cualquier barbero disponible',
      placeholders: {
        name: 'Ej. Luis Ortega',
        phone: '(863) 000-0000',
        notes: 'Ej. Quiero fade bajo y perfilado de barba.',
      },
      serviceOptions: [
        'Corte para adulto',
        'Corte para niño',
        'Corte + barba',
        'Perfilado de barba',
        'Primer corte de bebé',
      ],
      submit: 'Solicitar mi cita',
      sending: 'Enviando…',
      disclaimer: 'Tu cita queda confirmada cuando te llamemos. Domingos cerrado.',
      success: {
        title: '¡Solicitud enviada!',
        thanks: 'Gracias',
        neighbor: 'vecino',
        confirmA: 'Te llamamos al',
        confirmB: 'para confirmar tu cita del',
        chosenDay: 'día elegido',
        with: 'con',
        again: 'Agendar otra cita',
      },
    },
    location: {
      eyebrow: 'Ubicación y contacto',
      title: 'Estamos en Main Street, Dundee',
      mapTitle: 'Mapa de Haircuts Factory en 316 Main Street, Dundee, FL',
      directions: 'Abrir en Google Maps',
      hoursTitle: 'Horario',
      today: 'Hoy',
      days: ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'],
      closed: 'Cerrado',
      languageNote: 'Atención en español e inglés.',
    },
    testimonials: {
      eyebrow: 'Testimonios',
      title: 'Lo que dicen las familias de Dundee',
      ratingLabel: 'Calificación 5 de 5 estrellas',
      items: [
        {
          name: 'Familia Pérez',
          role: 'Vecinos de Main Street',
          quote:
            'Venimos los tres juntos los sábados: mi esposo, mi hijo y yo. El lugar es cómodo, limpio y los nenes ya lo piden. Se siente como estar en familia.',
        },
        {
          name: 'Luis Ortega',
          role: 'Cliente desde el primer día',
          quote:
            'Llego sin cita, me atienden rápido y el fade siempre queda perfecto. Y poder hablar en español lo hace todo más fácil.',
        },
        {
          name: 'María Rodríguez',
          role: 'Mamá de dos, Dundee',
          quote:
            'Le cortan el pelo a mis dos hijos y salen felices. Nunca nos han apurado y siempre queda como uno pide.',
        },
        {
          name: 'Carmen Díaz',
          role: 'Abuela de Jayden, 6 años',
          quote:
            'Le hicieron el primer corte a mi nieto y hasta nos guardaron un mechoncito de recuerdo. Detalles así no se olvidan.',
        },
      ],
    },
    finalCta: {
      title: 'Te guardamos la silla',
      body:
        'Ven con tu familia, siéntate tranquilo y déjanos el resto. Aquí siempre hay alguien que te habla en español.',
      callPrefix: 'Llámanos:',
      note: 'Lunes a sábado · Con cita o por orden de llegada',
    },
    footer: {
      about:
        'Barbería familiar en Dundee, Florida. Cortes para niños y adultos, fades, barba y diseños.',
      contactTitle: 'Contacto',
      hoursShort: 'Lun–Vie 9am–7pm · Sáb 8am–6pm',
      sectionsTitle: 'Secciones',
      areaTitle: 'Área que servimos',
      areaText: 'Dundee, Lake Hamilton, Haines City, Winter Haven y todo Polk County.',
      rights: 'Haircuts Factory · Barbería en Dundee, FL',
      languages: 'Hablamos español · We speak English',
    },
    mobileBar: { call: 'Llamar', book: 'Agendar' },
  },

  en: {
    meta: {
      title: 'Haircuts Factory | Family Barbershop in Dundee, FL',
      description:
        'Family barbershop in Dundee, FL. Haircuts for kids and adults, fades, beard trims and designs. By appointment or walk-in. We speak Spanish and English. Call +1 787-429-0831.',
    },
    nav: {
      about: 'About us',
      services: 'Services',
      barbers: 'Barbers',
      gallery: 'Gallery',
      how: 'How it works',
      book: 'Book',
      location: 'Location',
    },
    barbers: {
      eyebrow: 'Our team',
      title: 'Pick your barber',
      intro:
        'Tap the photo of the barber you want and we take you to the booking form with his name already selected.',
      roles: {
        alex: "Owner · Kids' cuts and fades",
        gardy: 'Barber · Fades and classics',
        michael: 'Barber · Beards and designs',
        cuba: 'Barber · Blends and fades',
        yady: 'Barber · Fades and line-ups',
        malvin: 'Barber · Curls and designs',
      },
      bookWith: 'Book with',
      tapHint: 'Tap to book',
      selected: 'Selected',
      clear: 'Clear selection',
    },
    header: {
      locationLabel: 'Dundee, FL',
      call: 'Call us',
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
      langSwitch: 'Ver en español',
    },
    hero: {
      eyebrow: 'Family barbershop · Dundee, FL',
      titleTop: 'Your trusted barbershop in Dundee',
      titleAccent: 'for the whole family',
      subtitleLead:
        'Professional haircuts for kids and adults: fades, scissor cuts, beard trims and designs. Appointments or walk-ins.',
      subtitleStrong: 'We speak Spanish and English!',
      ctaBook: 'Book your spot today',
      ctaCall: 'Call us now',
      facts: ['Spanish & English', 'Appointments or walk-ins', 'Monday to Saturday'],
      imageAlt: 'Inside Haircuts Factory with its barber chairs and LED lighting',
    },
    about: {
      eyebrow: 'About us',
      title: 'We are a family barbershop',
      p1: 'Haircuts Factory started with something simple: we wanted a place where a whole family could sit comfortably, be understood in their own language, and walk out with a haircut they were proud of.',
      p2: 'Dad rushing in before work, the little one getting a first haircut, and grandpa on his usual visit all get the same care. A great cut, good conversation, and a relaxed room for everyone.',
      highlight:
        'We serve you in Spanish or English, whichever you prefer — so you can explain exactly how you want your cut, with no guessing.',
      imageAlt: 'Inside Haircuts Factory with its barber chairs and LED lighting',
      values: [
        { title: 'Family', text: 'Grandpa, dad and the kids all sit in our chairs — and all leave happy.' },
        {
          title: 'Community',
          text: 'We are your neighbors on Main Street and we know our clients by name.',
        },
        {
          title: 'Trust',
          text: 'Sanitized tools, clean work and clear prices from the very start.',
        },
      ],
    },
    services: {
      eyebrow: 'Services & prices',
      title: 'Services & prices',
      intro:
        'Clear prices from the start. Every service includes edge-ups and a relaxed atmosphere — no rushing.',
      badge: 'Premium',
      priceVaries: 'Price varies',
      imageAlt: 'Haircuts Factory price list',
      note: 'Prices may vary with hair length and type. Just ask us — no commitment.',
      items: {
        haircut: { title: 'Hair cut' },
        haircutBeardTowel: { title: 'Hair cut + beard', detail: 'With hot towel' },
        haircutBeardSteamer: { title: 'Hair cut + beard', detail: 'Hot towel and steamer' },
        kids: { title: "Kid's cut" },
        beardEyebrows: { title: 'Beard + eyebrows' },
        eyebrows: { title: 'Eyebrows' },
        senior: { title: 'Senior special', detail: 'Wednesdays only' },
        shampoo: { title: 'Shampoo' },
        vip: {
          title: 'VIP service',
          detail: 'Haircut, shave with hot towel and steamer, massage and black mask',
        },
        platinum: { title: 'Platinum hair', detail: 'Haircut included' },
      },
    },
    gallery: {
      eyebrow: 'Our work',
      title: 'Cuts done in our chair',
      intro: 'Real photos of our work. Bring any reference you like and we will match it.',
      regionLabel: 'Carousel of finished haircut photos',
      photos: [
        { caption: 'The owner at work', alt: 'The owner of Haircuts Factory cutting a client’s hair' },
        { caption: 'High fade with beard line-up', alt: 'Adult client with a high fade and a shaped beard' },
        { caption: 'Clipper and comb work', alt: 'Barber using clippers on a client’s haircut' },
        { caption: 'Blend with defined curls', alt: 'Haircut with a clean blend and defined curls on top' },
        { caption: 'Classic taper, clean line', alt: 'Back view of a classic haircut with a clean taper' },
      ],
    },
    steps: {
      eyebrow: 'How it works',
      title: 'Three steps and you are in the chair',
      stepLabel: 'Step',
      items: [
        {
          title: 'Call or text us',
          description:
            'Dial +1 787-429-0831 or send us a WhatsApp. You can also book right here in the form below.',
        },
        {
          title: 'Appointment or walk-in',
          description:
            'With an appointment we take you at your time. If you just stop by, we take you in order of arrival.',
        },
        {
          title: 'Done — walk out with a great cut!',
          description: 'You sit down, we talk it through, and you leave with the cut you wanted. Simple.',
        },
      ],
    },
    booking: {
      eyebrow: 'Book an appointment',
      title: 'Book your spot today',
      intro:
        'Leave us your details and we will confirm by phone or WhatsApp the same day. If you would rather talk to us directly, give us a call.',
      callLabel: 'Call us',
      callAction: 'Call now',
      whatsappLabel: 'WhatsApp',
      whatsappAction: 'Message us now',
      walkIn: 'We also take walk-ins Monday through Saturday. No appointment needed to stop by.',
      labels: {
        name: 'Full name',
        phone: 'Phone',
        barber: 'Barber',
        service: 'Service',
        date: 'Date',
        time: 'Time',
        notes: 'Anything we should know? (optional)',
      },
      anyBarber: 'Any available barber',
      placeholders: {
        name: 'e.g. Luis Ortega',
        phone: '(863) 000-0000',
        notes: 'e.g. Low fade and a beard line-up.',
      },
      serviceOptions: [
        'Adult haircut',
        "Kid's haircut",
        'Haircut + beard',
        'Beard line-up',
        'Baby first haircut',
      ],
      submit: 'Request my appointment',
      sending: 'Sending…',
      disclaimer: 'Your appointment is confirmed once we call you. Closed on Sundays.',
      success: {
        title: 'Request sent!',
        thanks: 'Thank you',
        neighbor: 'neighbor',
        confirmA: 'We will call you at',
        confirmB: 'to confirm your appointment on',
        chosenDay: 'the day you picked',
        with: 'with',
        again: 'Book another appointment',
      },
    },
    location: {
      eyebrow: 'Location & contact',
      title: 'Find us on Main Street, Dundee',
      mapTitle: 'Map of Haircuts Factory at 316 Main Street, Dundee, FL',
      directions: 'Open in Google Maps',
      hoursTitle: 'Hours',
      today: 'Today',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      closed: 'Closed',
      languageNote: 'We serve you in Spanish and English.',
    },
    testimonials: {
      eyebrow: 'Testimonials',
      title: 'What Dundee families say',
      ratingLabel: 'Rated 5 out of 5 stars',
      items: [
        {
          name: 'The Pérez family',
          role: 'Neighbors on Main Street',
          quote:
            'The three of us come together on Saturdays: my husband, my son and me. The place is comfortable, clean, and the kids ask to go. It feels like family.',
        },
        {
          name: 'Luis Ortega',
          role: 'Client since day one',
          quote:
            'I walk in without an appointment, they take me quickly, and the fade is always perfect. Being able to talk in Spanish makes it all easier.',
        },
        {
          name: 'María Rodríguez',
          role: 'Mom of two, Dundee',
          quote:
            'They cut both my boys’ hair and they leave happy. They have never rushed us and it always comes out just how we ask.',
        },
        {
          name: 'Carmen Díaz',
          role: 'Grandma of Jayden, 6',
          quote:
            'They gave my grandson his first haircut and even saved us a little lock of hair. You do not forget details like that.',
        },
      ],
    },
    finalCta: {
      title: 'We are saving you a chair',
      body:
        'Bring the family, sit back, and leave the rest to us. There is always someone here who speaks your language.',
      callPrefix: 'Call us:',
      note: 'Monday to Saturday · Appointments or walk-ins',
    },
    footer: {
      about:
        'Family barbershop in Dundee, Florida. Haircuts for kids and adults, fades, beard trims and designs.',
      contactTitle: 'Contact',
      hoursShort: 'Mon–Fri 9am–7pm · Sat 8am–6pm',
      sectionsTitle: 'Sections',
      areaTitle: 'Areas we serve',
      areaText: 'Dundee, Lake Hamilton, Haines City, Winter Haven and all of Polk County.',
      rights: 'Haircuts Factory · Barbershop in Dundee, FL',
      languages: 'We speak English · Hablamos español',
    },
    mobileBar: { call: 'Call', book: 'Book' },
  },
}
